import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSkillMemoryStore } from "@/application/stores/skillMemoryStore";
import { buildReviewPlaylist } from "@/application/usecases/buildReviewPlaylist";
import { getSharedContentLoader } from "@/application/usecases/usePreloadLevel";
import type { Drill } from "@/domain/learning/model/drill";
import type { Level } from "@/domain/learning/model/level";
import { skillKeyOf } from "@/domain/learning/retention-schedule";
import { withSkillPractice } from "@/domain/learning/skill-memory";
import type { SkillMemoryRepository } from "@/domain/learning/skill-memory-repository";
import { commandSetFromLevels } from "@/domain/vim/model/command-set";
import { createSkillMemoryRepository } from "@/infrastructure/persistence/skill-memory-repository-factory";
import { Button } from "@/presentation/components/Button";
import { Icon } from "@/presentation/components/icons/Icon";
import { HeaderZone, MainZone } from "@/presentation/components/layout/Zones";
import { useBgm } from "@/presentation/features/bgm/useBgm";
import { DrillErrorBoundary } from "@/presentation/features/drill/DrillErrorBoundary";
import { DrillView } from "@/presentation/features/drill/DrillView";
import type { Route } from "@/presentation/router/route";
import "./ReviewScreen.css";

interface ReviewScreenProps {
  readonly navigate: (route: Route) => void;
}

type LoadState =
  | { readonly status: "loading" }
  | { readonly status: "empty" }
  | { readonly status: "error" }
  | {
      readonly status: "ready";
      readonly playlist: readonly Drill[];
      readonly levels: readonly Level[];
    };

/**
 * S-4 復習の刻。期限到来スキルを短時間で想起するセッション（F-011）。
 * 朱印やクリア集合は触らず、スキル記憶だけを更新する。
 */
export function ReviewScreen({ navigate }: ReviewScreenProps) {
  const memory = useSkillMemoryStore((state) => state.memory);
  const setMemory = useSkillMemoryStore((state) => state.setMemory);
  const [loadState, setLoadState] = useState<LoadState>({ status: "loading" });
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(false);
  const [drillKey, setDrillKey] = useState(0);
  useBgm("keiko");

  const repositoryRef = useRef<SkillMemoryRepository | null>(null);
  if (repositoryRef.current === null) {
    repositoryRef.current = createSkillMemoryRepository();
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loader = getSharedContentLoader();
        const summaries = await loader.loadManifest();
        const levels = await Promise.all(summaries.map((summary) => loader.loadLevel(summary.id)));
        if (cancelled) {
          return;
        }
        // 入場時点のメモリで課題を組む。セッション中の更新で組み直さない。
        const playlist = buildReviewPlaylist(levels, useSkillMemoryStore.getState().memory);
        if (playlist.length === 0) {
          setLoadState({ status: "empty" });
          return;
        }
        setLoadState({ status: "ready", playlist, levels });
      } catch {
        if (!cancelled) {
          setLoadState({ status: "error" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const commandSet = useMemo(() => {
    if (loadState.status !== "ready") {
      return commandSetFromLevels([]);
    }
    return commandSetFromLevels(loadState.levels.map((level) => level.commands));
  }, [loadState]);

  const currentDrill =
    loadState.status === "ready" && !done ? loadState.playlist[index] : undefined;

  const completeCurrent = useCallback(() => {
    if (loadState.status !== "ready" || currentDrill === undefined) {
      return;
    }
    const key = skillKeyOf(currentDrill);
    if (key !== null) {
      const nextMemory = withSkillPractice(memory, key);
      repositoryRef.current?.save(nextMemory);
      setMemory(nextMemory);
    }
    if (index + 1 >= loadState.playlist.length) {
      setDone(true);
      return;
    }
    setIndex((value) => value + 1);
    setDrillKey((value) => value + 1);
  }, [currentDrill, index, loadState, memory, setMemory]);

  return (
    <>
      <HeaderZone>
        <button
          type="button"
          className="review-screen__back"
          onClick={() => navigate({ name: "title" })}
        >
          <Icon name="back" decorative size={20} />
          <span>門へ</span>
        </button>
        <span className="review-screen__title">
          復習の刻
          {loadState.status === "ready" && !done
            ? ` · ${index + 1}/${loadState.playlist.length}`
            : ""}
        </span>
      </HeaderZone>
      <MainZone className="review-screen__main">
        {loadState.status === "loading" && <p className="review-screen__message">読み込み中…</p>}
        {loadState.status === "error" && (
          <p className="review-screen__message">復習の課題を読み込めませんでした。</p>
        )}
        {loadState.status === "empty" && (
          <div className="review-screen__empty">
            <p>いま復習どきの技はない。腕は鈍っていないようだ。</p>
            <Button variant="primary" onClick={() => navigate({ name: "title" })}>
              門へ戻る
            </Button>
          </div>
        )}
        {done && (
          <div className="review-screen__empty">
            <p>復習はここまで。指に残った感触を忘れぬうちに本稽古へ戻れ。</p>
            <Button variant="primary" onClick={() => navigate({ name: "title" })}>
              門へ戻る
            </Button>
          </div>
        )}
        {currentDrill !== undefined && (
          <DrillErrorBoundary onRetry={() => setDrillKey((n) => n + 1)}>
            <DrillView
              key={`${drillKey}-${currentDrill.id}`}
              drill={currentDrill}
              commandSet={commandSet}
              variant="drill"
              onPassed={completeCurrent}
              progress={{
                current: index + 1,
                total: loadState.status === "ready" ? loadState.playlist.length : 1,
              }}
            />
          </DrillErrorBoundary>
        )}
      </MainZone>
    </>
  );
}
